import { HonchConfig, HonchInstance, DEFAULT_CONFIG } from './config';
import { EventQueue } from './core/queue';
import { Transport } from './core/transport';
import { Storage } from './core/storage';
import { AutoCapture } from './autocapture';
import { SessionRecording } from './recording';
import { WebVitals } from './vitals';
import { getBrowserInfo, getDeviceInfo } from './utils/browser';
import { generateUUID } from './utils/uuid';
import { Logger } from './utils/logger';

export class Honch implements HonchInstance {
    private config: HonchConfig;
    private queue: EventQueue;
    private transport: Transport;
    private storage: Storage;
    private sessionStorage: Storage;
    private logger: Logger;

    private autoCapture: AutoCapture | null = null;
    private sessionRecording: SessionRecording | null = null;
    private webVitals: WebVitals | null = null;
    private sessionCheckTimer: ReturnType<typeof setInterval> | null = null;
    private boundSessionVisibilityHandler: (() => void) | null = null;

    private distinctId: string;
    private sessionId: string;
    private windowId: string;
    private isIdentified: boolean = false;
    private personProperties: Record<string, unknown> = {};
    private superProperties: Record<string, unknown> = {};
    private timezone: string = '';
    private optedOut: boolean = false;
    private initialized: boolean = false;
    private shouldRecordSession: boolean = false;

    // Prevent concurrent session rotations from racing
    private isRotatingSession: boolean = false;

    constructor() {
        this.config = { ...DEFAULT_CONFIG, token: '' };
        this.storage = new Storage('memory', 'honch_');
        this.sessionStorage = new Storage('memory', 'honch_sess_');
        this.logger = new Logger(false);
        this.transport = new Transport('', '', this.logger);
        this.queue = new EventQueue(this.transport, this.logger, 10, 5000, 3);
        this.distinctId = '';
        this.sessionId = '';
        this.windowId = '';
    }

    init(token: string, options: Partial<Omit<HonchConfig, 'token'>> = {}): HonchInstance {
        if (this.initialized) {
            this.logger.warn('Honch already initialized. Call reset() first to reinitialize.');
            return this;
        }

        if (!token) {
            this.logger.error('Honch.init() requires a valid API token');
            return this;
        }

        this.config = { ...DEFAULT_CONFIG, ...options, token };
        this.logger = new Logger(this.config.debug);
        this.logger.info('Initializing Honch SDK', { token: token.substring(0, 10) + '...' });

        if (this.config.respect_dnt && this.isDoNotTrackEnabled()) {
            this.logger.info('Do Not Track enabled, disabling tracking');
            this.config.disabled = true;
        }

        if (this.config.disabled) {
            this.logger.info('Honch tracking is disabled');
            this.initialized = true;
            return this;
        }

        // Setup storage
        this.storage = new Storage(this.config.persistence, this.config.storage_prefix);
        this.sessionStorage = new Storage('sessionStorage', this.config.storage_prefix + 'sess_');

        // Setup transport
        this.transport = new Transport(
            this.config.api_host,
            this.config.token,
            this.logger,
            this.config.fetch_fn,
            this.config.session_recording_compression ?? true
        );

        // Setup queue — respect the user's persistence setting
        this.queue = new EventQueue(
            this.transport,
            this.logger,
            this.config.batch_size,
            this.config.flush_interval_ms,
            this.config.max_retries,
            this.config.persistence,
            this.config.storage_prefix + 'queue_'
        );

        // Load or generate IDs
        this.distinctId = this.storage.get('distinct_id') || this.generateId();
        this.storage.set('distinct_id', this.distinctId);
        this.isIdentified = this.storage.get('identified') === 'true';

        this.sessionId = this.getOrCreateSessionId();
        this.windowId = generateUUID();

        // Load persisted data
        this.personProperties = this.storage.get('person_properties') || {};
        this.superProperties = this.storage.get('super_properties') || {};
        this.optedOut = this.storage.get('opted_out') === true;

        if (this.optedOut) {
            this.logger.info('User has opted out of tracking');
            this.initialized = true;
            return this;
        }

        // Sampling decision once per page load
        this.shouldRecordSession = this.config.session_recording &&
            Math.random() < this.config.session_recording_sample_rate;

        // Start queue
        this.queue.start();

        // Session timeout check every 5 minutes
        this.sessionCheckTimer = setInterval(() => {
            this.checkAndRefreshSession();
        }, 5 * 60 * 1000);

        // Check session timeout when tab becomes visible
        if (typeof document !== 'undefined') {
            this.boundSessionVisibilityHandler = () => {
                if (document.visibilityState === 'visible') {
                    this.checkAndRefreshSession();
                }
            };
            document.addEventListener('visibilitychange', this.boundSessionVisibilityHandler);
        }

        this.timezone = typeof Intl !== 'undefined'
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : '';

        this.initialized = true;
        if (this.shouldRecordSession) {
            this.startSessionRecording();
        }

        // Setup autocapture AFTER recording is initialized
        if (this.config.autocapture || this.config.capture_clicks || this.config.capture_forms) {
            this.autoCapture = new AutoCapture(this, this.config, this.logger);
            this.autoCapture.start();
        }

        // Setup Web Vitals
        if (this.config.capture_vitals) {
            this.webVitals = new WebVitals(this);
            this.webVitals.start();
        }

        // Track initial pageview AFTER recording is capturing
        if (this.config.autocapture) {
            this.capturePageview();
        }

        this.logger.info('Honch SDK initialized', { distinctId: this.distinctId });

        if (this.config.loaded) {
            this.config.loaded(this);
        }

        return this;
    }

    /**
     * Start (or restart) session recording with current IDs.
     * Properly awaits the old recording's teardown before starting a new one
     * to prevent rrweb observer conflicts.
     */
    private async startSessionRecording(): Promise<void> {
        // Stop any existing recording first and WAIT for rrweb to clean up
        if (this.sessionRecording) {
            await this.sessionRecording.stop();
            this.sessionRecording = null;
        }

        this.sessionRecording = new SessionRecording(
            this.transport,
            this.distinctId,
            this.sessionId,
            this.windowId,
            this.logger
        );
        this.sessionRecording.start();
    }

    _onNavigate(url: string): void {
        if (this.sessionRecording) {
            this.sessionRecording.onNavigate(url);
        }
    }

    capture(event: string, properties: Record<string, unknown> = {}): void {
        if (!this.initialized || this.config.disabled || this.optedOut) {
            return;
        }

        if (!event || typeof event !== 'string') {
            this.logger.warn('capture() requires a valid event name');
            return;
        }

        if (typeof window !== 'undefined' && this.shouldExcludeUrl(window.location.href)) {
            this.logger.debug('URL excluded from tracking', { url: window.location.href });
            return;
        }

        // Check and refresh session on interaction
        this.checkAndRefreshSession();
        this.updateSessionActivity();

        const eventData = {
            event,
            distinct_id: this.distinctId,
            properties: this.buildEventProperties(properties),
            timestamp: new Date().toISOString(),
        };

        this.logger.debug('Capturing event', eventData);
        this.queue.enqueue(eventData);
    }

    identify(distinctId: string, properties: Record<string, unknown> = {}): void {
        if (!this.initialized || this.config.disabled || this.optedOut) {
            return;
        }

        if (!distinctId || typeof distinctId !== 'string') {
            this.logger.warn('identify() requires a valid distinct_id');
            return;
        }

        if (this.isIdentified && this.distinctId !== distinctId) {
            this.logger.warn(
                `User already identified as '${this.distinctId}'. Call reset() first to switch users.`
            );
            return;
        }

        const previousId = this.distinctId;
        this.distinctId = distinctId;
        this.storage.set('distinct_id', distinctId);
        this.isIdentified = true;
        this.storage.set('identified', 'true');

        this.personProperties = { ...this.personProperties, ...properties };
        this.storage.set('person_properties', this.personProperties);

        this.capture('$identify', {
            $anon_distinct_id: previousId,
            $set: properties,
        });

        if (this.sessionRecording) {
            this.sessionRecording.updateDistinctId(distinctId);
        }

        this.logger.info('User identified', { distinctId, previousId });
    }

    alias(alias: string, distinctId?: string): void {
        if (!this.initialized || this.config.disabled || this.optedOut) {
            return;
        }

        this.capture('$create_alias', {
            alias,
            distinct_id: distinctId || this.distinctId,
        });
    }

    reset(): void {
        this.logger.info('Resetting user');

        // Capture old recording reference so startSessionRecording can await its
        // teardown properly — prevents two rrweb instances running simultaneously.
        const oldRecording = this.sessionRecording;
        this.sessionRecording = null;

        if (this.sessionCheckTimer) {
            clearInterval(this.sessionCheckTimer);
            this.sessionCheckTimer = null;
        }

        if (this.boundSessionVisibilityHandler) {
            document.removeEventListener('visibilitychange', this.boundSessionVisibilityHandler);
            this.boundSessionVisibilityHandler = null;
        }

        this.queue.flush();

        this.storage.remove('distinct_id');
        this.storage.remove('person_properties');
        this.storage.remove('identified');
        this.sessionStorage.remove('session_id');
        this.sessionStorage.remove('session_start');
        this.sessionStorage.remove('last_activity');

        this.isIdentified = false;
        this.distinctId = this.generateId();
        this.storage.set('distinct_id', this.distinctId);
        this.sessionId = this.createNewSession();
        this.windowId = generateUUID();

        this.personProperties = {};

        if (this.shouldRecordSession) {
            // Properly sequence: await old teardown, then start new.
            // startSessionRecording checks this.sessionRecording (now null),
            // so we manually await the old one first.
            const startNew = async () => {
                if (oldRecording) {
                    await oldRecording.stop();
                }
                this.sessionRecording = new SessionRecording(
                    this.transport,
                    this.distinctId,
                    this.sessionId,
                    this.windowId,
                    this.logger
                );
                this.sessionRecording.start();
            };
            startNew();
        } else if (oldRecording) {
            oldRecording.stop();
        }

        // Restart session check timer
        this.sessionCheckTimer = setInterval(() => {
            this.checkAndRefreshSession();
        }, 5 * 60 * 1000);

        if (typeof document !== 'undefined') {
            this.boundSessionVisibilityHandler = () => {
                if (document.visibilityState === 'visible') {
                    this.checkAndRefreshSession();
                }
            };
            document.addEventListener('visibilitychange', this.boundSessionVisibilityHandler);
        }
    }

    setPersonProperties(properties: Record<string, unknown>): void {
        if (!this.initialized || this.config.disabled || this.optedOut) {
            return;
        }

        this.personProperties = { ...this.personProperties, ...properties };
        this.storage.set('person_properties', this.personProperties);
        this.capture('$set', { $set: properties });
    }

    setPersonPropertiesOnce(properties: Record<string, unknown>): void {
        if (!this.initialized || this.config.disabled || this.optedOut) {
            return;
        }

        const newProps: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(properties)) {
            if (!(key in this.personProperties)) {
                newProps[key] = value;
            }
        }

        if (Object.keys(newProps).length > 0) {
            this.personProperties = { ...this.personProperties, ...newProps };
            this.storage.set('person_properties', this.personProperties);
            this.capture('$set', { $set_once: newProps });
        }
    }

    register(properties: Record<string, unknown>): void {
        this.superProperties = { ...this.superProperties, ...properties };
        this.storage.set('super_properties', this.superProperties);
    }

    unregister(key: string): void {
        delete this.superProperties[key];
        this.storage.set('super_properties', this.superProperties);
    }

    getDistinctId(): string {
        return this.distinctId;
    }

    getSessionId(): string {
        return this.sessionId;
    }

    async flush(): Promise<void> {
        await this.queue.flush();
    }

    opt_out(): void {
        this.optedOut = true;
        this.storage.set('opted_out', true);

        if (this.autoCapture) {
            this.autoCapture.stop();
            this.autoCapture = null;
        }

        if (this.sessionRecording) {
            this.sessionRecording.stop();
            this.sessionRecording = null;
        }

        if (this.webVitals) {
            this.webVitals.stop();
            this.webVitals = null;
        }

        if (this.sessionCheckTimer) {
            clearInterval(this.sessionCheckTimer);
            this.sessionCheckTimer = null;
        }

        if (this.boundSessionVisibilityHandler) {
            document.removeEventListener('visibilitychange', this.boundSessionVisibilityHandler);
            this.boundSessionVisibilityHandler = null;
        }

        this.queue.stop();
        this.logger.info('User opted out of tracking');
    }

    opt_in(): void {
        this.optedOut = false;
        this.storage.remove('opted_out');

        if (this.config.autocapture && this.autoCapture) {
            this.autoCapture.start();
        }
        this.queue.start();

        // Restart session recording if it was stopped by opt_out
        if (this.shouldRecordSession && !this.sessionRecording) {
            this.startSessionRecording();
        }

        this.logger.info('User opted in to tracking');
    }

    isOptedOut(): boolean {
        return this.optedOut;
    }

    debug(enabled: boolean = true): void {
        this.logger.setDebug(enabled);
        this.config.debug = enabled;
    }

    // ==========================================================================
    // Private Methods
    // ==========================================================================

    private generateId(): string {
        return generateUUID();
    }

    private getOrCreateSessionId(): string {
        const existingSessionId = this.sessionStorage.get('session_id') as string | null;
        const lastActivity = this.sessionStorage.get('last_activity') as number | null;
        const now = Date.now();
        const SESSION_TIMEOUT = 30 * 60 * 1000;

        if (existingSessionId && lastActivity && now - lastActivity < SESSION_TIMEOUT) {
            this.sessionStorage.set('last_activity', now);
            return existingSessionId;
        }

        return this.createNewSession();
    }

    private createNewSession(): string {
        const sessionId = `sess_${generateUUID()}`;
        const now = Date.now();

        this.sessionStorage.set('session_id', sessionId);
        this.sessionStorage.set('session_start', now);
        this.sessionStorage.set('last_activity', now);

        this.logger.debug('Created new session', { sessionId });
        return sessionId;
    }

    private updateSessionActivity(): void {
        this.sessionStorage.set('last_activity', Date.now());
    }

    /**
     * Check if the current session has timed out and create a new one if so.
     * Uses a lock to prevent concurrent rotations from racing.
     */
    private checkAndRefreshSession(): void {
        if (this.isRotatingSession || !this.initialized || this.optedOut) return;

        const lastActivity = this.sessionStorage.get('last_activity') as number | null;
        const now = Date.now();
        const SESSION_TIMEOUT = 30 * 60 * 1000;

        if (lastActivity && now - lastActivity >= SESSION_TIMEOUT) {
            // Defer rotation while the tab is hidden
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                this.logger.debug('Session timed out but tab is hidden, deferring rotation');
                return;
            }

            this.isRotatingSession = true;
            this.logger.info('Session timed out, creating new session');

            this.sessionId = this.createNewSession();
            this.windowId = generateUUID();

            if (this.shouldRecordSession) {
                // startSessionRecording is async — it awaits the old recording's
                // teardown before starting a new one
                this.startSessionRecording().finally(() => {
                    this.isRotatingSession = false;
                });
            } else {
                if (this.sessionRecording) {
                    this.sessionRecording.stop();
                    this.sessionRecording = null;
                }
                this.isRotatingSession = false;
            }
        }
    }

    private capturePageview(): void {
        if (typeof window === 'undefined') return;

        this.capture('$pageview', {
            $current_url: window.location.href,
            $pathname: window.location.pathname,
            $search: window.location.search,
            $hash: window.location.hash,
            $title: document.title,
            $referrer: document.referrer,
        });
    }

    private buildEventProperties(customProperties: Record<string, unknown>): Record<string, unknown> {
        if (typeof window === 'undefined') return { ...customProperties };

        const browserInfo = getBrowserInfo();
        const deviceInfo = getDeviceInfo();

        return {
            $session_id: this.sessionId,
            $window_id: this.windowId,

            $browser: browserInfo.name,
            $browser_version: browserInfo.version,
            $os: deviceInfo.os,
            $os_version: deviceInfo.osVersion,
            $device_type: deviceInfo.type,
            $screen_height: window.screen.height,
            $screen_width: window.screen.width,
            $viewport_height: window.innerHeight,
            $viewport_width: window.innerWidth,

            $current_url: window.location.href,
            $host: window.location.host,
            $pathname: window.location.pathname,

            $timezone: this.timezone,

            $lib: 'javascript',
            $lib_version: __SDK_VERSION__,

            $timestamp: new Date().toISOString(),
            $time: Date.now(),

            ...this.superProperties,
            ...this.config.default_properties,
            ...customProperties,
        };
    }

    private shouldExcludeUrl(url: string): boolean {
        return this.config.exclude_urls.some((pattern) => pattern.test(url));
    }

    private isDoNotTrackEnabled(): boolean {
        if (typeof navigator === 'undefined') return false;
        const dnt = navigator.doNotTrack || (window as any).doNotTrack;
        return dnt === '1' || dnt === 'yes';
    }
}